import React from 'react';
import { ArrowRight, Play, Camera, Instagram } from 'lucide-react';
export function DocumentaryVariant() {
  return <div className="min-h-screen w-full bg-white text-slate-900 font-sans selection:bg-blue-100" data-id="element-17">
      {/* Immersive Header */}
      <header className="relative h-[85vh] w-full overflow-hidden" data-id="element-18">
        <div className="absolute inset-0" data-id="element-19">
          <img src="https://images.unsplash.com/photo-1552053831-71594a27632d?q=80&w=2062&auto=format&fit=crop" alt="Dog walking in nature" className="w-full h-full object-cover" data-id="element-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-21"></div>
        </div>

        <nav className="absolute top-0 w-full p-6 flex justify-between items-center z-20 text-white" data-id="element-22">
          <div className="font-bold tracking-tighter text-2xl" data-id="element-23">ChillHuaHua</div>
          <button className="bg-white/20 backdrop-blur-md hover:bg-white/30 px-6 py-2 rounded-full text-sm font-medium transition-all" data-id="element-24">
            Shop Harness
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 w-full p-8 md:p-16 z-10 max-w-4xl" data-id="element-25">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-white/90 text-xs font-medium mb-6 border border-white/20" data-id="element-26">
            <Play size={12} fill="currentColor" data-id="element-27" />
            <span data-id="element-28">5-10 min read</span>
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6 tracking-tight" data-id="element-29">
            If Your Dog Ever Coughs, Gags, or “Honks” on Walks…
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-light max-w-2xl leading-relaxed" data-id="element-30">
            Read this before you clip the leash on again. The “walk-time cough”
            most dog parents ignore… until it scares the hell out of them.
          </p>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto" data-id="element-31">
        {/* Intro Section - Split Layout */}
        <section className="grid md:grid-cols-12 gap-12 py-24 px-6 md:px-12 items-start" data-id="element-32">
          <div className="md:col-span-5 sticky top-24" data-id="element-33">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl" data-id="element-34">
              <img src="https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?q=80&w=2069&auto=format&fit=crop" alt="Concerned dog owner" className="w-full h-full object-cover" data-id="element-35" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur p-4 rounded-xl" data-id="element-36">
                <p className="text-sm font-medium text-slate-800" data-id="element-37">
                  "It feels like you just hurt your dog. Guilt. Panic.
                  Self-blame."
                </p>
              </div>
            </div>
          </div>

          <div className="md:col-span-7 prose prose-lg prose-slate max-w-none" data-id="element-38">
            <h2 className="text-3xl font-bold text-slate-900 mb-8" data-id="element-39">
              It usually starts small.
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed mb-8" data-id="element-40">
              A tiny hack-hack when your dog gets excited. A brief gag when they
              hit the end of the leash. A weird little “honking” sound that
              disappears as quickly as it came.
            </p>
            <p data-id="element-41">
              You tell yourself: “He’s just excited.” “It’s normal.” “He’s
              fine—he’s always done that.”
            </p>
            <p className="font-medium text-slate-900 border-l-4 border-blue-500 pl-6 my-8" data-id="element-42">
              And then one day it happens harder. Your dog lunges toward a
              squirrel… the leash snaps tight… and they cough like they’re
              trying to clear something stuck in their throat.
            </p>
            <p data-id="element-43">
              That moment hits different. Because it’s not just “annoying.” It
              feels like you just hurt your dog. And if you’re the kind of
              person who treats your dog like family, your brain does the thing:
              Guilt. Panic. Self-blame.
            </p>
            <p className="text-2xl font-bold text-slate-900 mt-12" data-id="element-44">
              The uncomfortable truth: most people are walking dogs using the
              most vulnerable part of their body.
            </p>
          </div>
        </section>

        {/* Visual Break - Full Width */}
        <section className="bg-slate-900 text-white py-24 px-6 md:px-12 overflow-hidden relative" data-id="element-45">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-600/10 rounded-l-full blur-3xl" data-id="element-46"></div>

          <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-47">
            <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-48">
              A dog’s neck is not designed to be a load-bearing handle.
            </h2>
            <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto" data-id="element-49">
              It’s delicate. It contains the trachea, nerves, blood vessels, and
              structures that aren’t meant to absorb sudden, repeated leash
              force.
            </p>

            <div className="grid md:grid-cols-3 gap-8 text-left" data-id="element-50">
              {[{
              title: 'Physical Stress',
              desc: 'Direct impact on the throat causing coughing and gagging.'
            }, {
              title: 'Opposition Reflex',
              desc: 'Dogs push into pressure, making pulling worse.'
            }, {
              title: 'Emotional Toll',
              desc: 'The owner feels guilt, the dog feels stress.'
            }].map((item, i) => <div key={i} className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors" data-id="element-51">
                  <div className="text-blue-400 font-bold text-lg mb-2" data-id="element-52">
                    0{i + 1}
                  </div>
                  <h3 className="font-bold text-xl mb-2" data-id="element-53">{item.title}</h3>
                  <p className="text-slate-400 text-sm" data-id="element-54">{item.desc}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* The Solution - Gallery Style */}
        <section className="py-24 px-6 md:px-12" data-id="element-55">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-56">
            <h2 className="text-3xl font-bold mb-6" data-id="element-57">The “ChestFlow” Concept</h2>
            <p className="text-lg text-slate-600" data-id="element-58">
              ChillHuaHua is built around a straightforward idea: Dogs are built
              to pull from the chest and shoulders… not the throat.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-59">
            <div className="relative group overflow-hidden rounded-2xl aspect-square" data-id="element-60">
              <img src="https://images.unsplash.com/photo-1601758228041-f3b2795255f1?q=80&w=2070&auto=format&fit=crop" alt="Dog running free" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-61" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-8" data-id="element-62">
                <div className="text-white" data-id="element-63">
                  <h3 className="text-2xl font-bold mb-2" data-id="element-64">No Neck Pressure</h3>
                  <p className="text-white/80" data-id="element-65">
                    Routes force through the chest/torso — the engine.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square" data-id="element-66">
              <img src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?q=80&w=2069&auto=format&fit=crop" alt="Happy dog on walk" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-67" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-8" data-id="element-68">
                <div className="text-white" data-id="element-69">
                  <h3 className="text-2xl font-bold mb-2" data-id="element-70">Escape Secure</h3>
                  <p className="text-white/80" data-id="element-71">
                    Designed so they can't back out when spooked.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof - Instagram Style */}
        <section className="bg-slate-50 py-24 px-6 md:px-12" data-id="element-72">
          <div className="max-w-6xl mx-auto" data-id="element-73">
            <div className="flex items-center justify-center gap-2 mb-12 text-slate-500" data-id="element-74">
              <Instagram size={20} data-id="element-75" />
              <span className="font-medium tracking-wide uppercase text-sm" data-id="element-76">
                Real Stories from the Community
              </span>
            </div>

            <div className="grid md:grid-cols-3 gap-6" data-id="element-77">
              {[{
              img: 'https://images.unsplash.com/photo-1517849845537-4d257902454a?q=80&w=1000&auto=format&fit=crop',
              text: 'I didn’t realize how much I was stressing about walks until I wasn’t anymore. The coughing stopped immediately.',
              user: '@golden_bailey'
            }, {
              img: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?q=80&w=1000&auto=format&fit=crop',
              text: 'I’ve tried multiple harnesses… this is the first one I actually trust. No more gagging.',
              user: '@max_the_beagle'
            }, {
              img: 'https://images.unsplash.com/photo-1530281700549-e82e7bf110d6?q=80&w=1000&auto=format&fit=crop',
              text: 'My dog used to back out of harnesses. I can walk without that constant fear now.',
              user: '@luna_adventures'
            }].map((post, i) => <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100" data-id="element-78">
                  <div className="aspect-square rounded-lg overflow-hidden mb-4" data-id="element-79">
                    <img src={post.img} alt="Dog testimonial" className="w-full h-full object-cover" data-id="element-80" />
                  </div>
                  <p className="text-sm text-slate-600 mb-3 leading-relaxed" data-id="element-81">
                    {post.text}
                  </p>
                  <p className="text-xs font-bold text-slate-900" data-id="element-82">
                    {post.user}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 md:px-12 text-center" data-id="element-83">
          <div className="max-w-2xl mx-auto" data-id="element-84">
            <h2 className="text-4xl font-bold mb-6" data-id="element-85">
              From guilt-walks to guilt-free walks.
            </h2>
            <p className="text-xl text-slate-600 mb-10" data-id="element-86">
              If you want to stop the coughing moments, protect your dog’s neck,
              and finally enjoy calm walks again.
            </p>
            <button className="bg-blue-600 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-xl shadow-blue-200 flex items-center gap-3 mx-auto" data-id="element-87">
              Check Availability
              <ArrowRight size={20} data-id="element-88" />
            </button>
            <p className="mt-6 text-sm text-slate-400" data-id="element-89">
              See the neck-friendly design for yourself
            </p>
          </div>
        </section>
      </main>
    </div>;
}